import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { UserContext } from 'providers/UserProvider';
import type React from 'react';
import {
  useEffect,
  useState,
  useMemo,
  createContext,
  type SetStateAction,
  type Dispatch,
  useContext,
  type JSX,
} from 'react';
import { isPaywallContent } from 'utils/paywallUtils';
import { type PaywallContext, defaultPaywallContext } from 'services/Paywall';

interface PaywallProps {
  children: JSX.Element;
  pianoPageConfig: PianoPageConfig;
}

const defaultSetPayWallState = (() => {}) as Dispatch<SetStateAction<PaywallContext>>;

export const UserPaywallContext = createContext({
  paywallState: defaultPaywallContext,
  setPaywallState: defaultSetPayWallState,
});

const PaywallProvider: React.FC<PaywallProps> = ({ children, pianoPageConfig }) => {
  const [paywallState, setPaywallState] = useState(defaultPaywallContext);
  const { userState, isBypassed, paywall30 } = useContext(UserContext);

  // Currently unused hook for observing paywall iframe events
  // Uncomment if paywall event tracking is needed in the future
  // usePaywallIframeObserver();

  useEffect(() => {
    const paywallContent = isPaywallContent(pianoPageConfig);

    let paywallCheck = paywall30;
    if (!paywallContent || (paywallContent && paywall30 === 'no-paywall') || isBypassed) {
      // Not paywallable content OR paywallable content and not paywalled
      paywallCheck = 'no-paywall';
    } else if (paywallContent && paywall30 === 'paywalled' && !isBypassed) {
      // Paywallable content and paywalled
      paywallCheck = 'paywalled';
    }

    setPaywallState({ paywallCheck });
  }, [isBypassed, paywall30, userState, pianoPageConfig]);

  const memoUserPaywallvalue = useMemo(() => ({ paywallState, setPaywallState }), [paywallState, setPaywallState]);

  return <UserPaywallContext.Provider value={memoUserPaywallvalue}>{children}</UserPaywallContext.Provider>;
};

export default PaywallProvider;
